import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Explicitly unregister datalabels if it exists to prevent unwanted labels on bars
if (ChartJS.registry?.plugins?.get('datalabels')) {
  ChartJS.unregister('datalabels');
}

const SpendingTrendChart = ({ spendingTrends, incomeTrends, isLoading, dateRange = null }) => {
  const navigate = useNavigate();
  
  // Handle bar click to drill down to month
  const handleChartClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const monthData = spendingTrends[elementIndex];
      
      if (monthData && monthData.month) {
        // Parse the month (format: YYYY-MM)
        const [year, month] = monthData.month.split('-');
        const startDate = new Date(year, parseInt(month) - 1, 1);
        const endDate = new Date(year, parseInt(month), 0); // Last day of month
        
        // Navigate to transactions with month filter
        const params = new URLSearchParams();
        params.set('startDate', startDate.toISOString().split('T')[0]);
        params.set('endDate', endDate.toISOString().split('T')[0]);
        
        console.log('📊 Drilling down to month:', monthData.month);
        navigate(`/transactions?${params.toString()}`);
      }
    }
  };
  
  // Handle hover for cursor
  const handleChartHover = (event, elements) => {
    event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
  };
  
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center overflow-hidden">
        <div className="animate-pulse text-gray-500">Loading spending trends...</div>
      </div>
    );
  }

  if (!spendingTrends || 
      !Array.isArray(spendingTrends) || 
      spendingTrends.length === 0 || 
      spendingTrends.every(item => !item.amount || item.amount === 0)) {
    return (
      <div className="h-full w-full flex items-center justify-center overflow-hidden">
        <div className="text-gray-500">No spending data available</div>
      </div>
    );
  }

  const totalSpending = spendingTrends.reduce((sum, item) => sum + (item.amount || 0), 0);
  if (totalSpending === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center overflow-hidden">
        <div className="text-gray-500">No spending activity to display</div>
      </div>
    );
  }

  // Use real income data from transactions (type='income')
  // If no income data available, show empty bars
  const incomeData = incomeTrends && incomeTrends.length > 0 ? incomeTrends : [];

  // Combine all unique months from both datasets
  const allMonths = Array.from(new Set([
    ...spendingTrends.map(item => item.month),
    ...incomeData.map(item => item.month)
  ])).sort();

  // Create month-to-value maps for easy lookup
  const spendingMap = {};
  const incomeMap = {};
  spendingTrends.forEach(item => spendingMap[item.month] = Math.abs(item.amount || 0));
  incomeData.forEach(item => incomeMap[item.month] = item.amount || 0);

  const data = {
    labels: allMonths,
    datasets: [
      {
        label: 'Monthly Spending',
        data: allMonths.map(month => spendingMap[month] || 0),
        backgroundColor: 'rgba(239, 68, 68, 0.5)', // Light red for spending
        borderColor: 'rgb(239, 68, 68)', // Red
        borderWidth: 1,
        borderRadius: 8,
        maxBarThickness: 60,
        barPercentage: 0.9, // Bars take up 90% of category width
        categoryPercentage: 1.0, // Categories touch each other
        // Disable any text on bars
        datalabels: {
          display: false
        }
      },
      {
        label: 'Cash In',
        data: allMonths.map(month => incomeMap[month] || 0),
        backgroundColor: 'rgba(34, 197, 94, 0.5)', // Light green for income
        borderColor: 'rgb(34, 197, 94)', // Green
        borderWidth: 1,
        borderRadius: 8,
        maxBarThickness: 60,
        barPercentage: 0.9, // Bars take up 90% of category width
        categoryPercentage: 1.0, // Categories touch each other
        // Disable any text on bars
        datalabels: {
          display: false
        }
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    onHover: handleChartHover,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          padding: 20,
          color: '#374151',
          font: {
            size: 12
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#374151',
        bodyColor: '#374151',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: $${context.parsed.y.toLocaleString()}`;
          },
          afterLabel: function() {
            return '\n💡 Click to view transactions';
          }
        }
      },
      // Disable any datalabels
      datalabels: {
        display: false
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          color: '#6B7280',
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`,
          color: '#6B7280'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  return (
    <div className="h-full w-full overflow-hidden">
      <Bar data={data} options={options} />
    </div>
  );
};

export default SpendingTrendChart; 